/* eslint-disable react/require-default-props */
import React, { Fragment, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ArrowPathIcon, ArrowsRightLeftIcon } from '@heroicons/react/20/solid';
import { useSyncEnrichedLeadDataMutation } from './enrichedLeadsAPI';
import { EnrichedLeadCompany, SyncEnrichedLeadRequest } from './enriched-leads-types';

interface EnrichedLeadSyncDataModalProps {
  show: boolean;
  setOpen: (flag: boolean) => void;
  company: EnrichedLeadCompany;
}

export default function EnrichedLeadSyncDataModal({ show, setOpen, company }: EnrichedLeadSyncDataModalProps) {
  const cancelButtonRef = useRef(null);
  const [syncData, { isLoading }] = useSyncEnrichedLeadDataMutation();

  const [syncOptions, setSyncOptions] = useState<SyncEnrichedLeadRequest>({
    syncDescription: true,
    syncFocusCategories: true,
    syncCoreTags: true,
    syncTerritories: true,
    syncLinkedIn: true,
  });

  const toggleOption = (key: keyof SyncEnrichedLeadRequest) => {
    setSyncOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSync = async () => {
    await syncData({ enrichedCompanyId: company.id, request: syncOptions });
    setOpen(false);
  };

  const options: { key: keyof SyncEnrichedLeadRequest; label: string; available: boolean }[] = [
    { key: 'syncDescription', label: 'Description', available: !!company.description },
    { key: 'syncLinkedIn', label: 'LinkedIn URL', available: !!company.linkedinUrl },
    { key: 'syncTerritories', label: 'Territory', available: !!company.territoryId },
    { key: 'syncFocusCategories', label: 'Focus Categories', available: !!company.focusCategories },
    { key: 'syncCoreTags', label: 'Core Tags', available: !!company.coreTags },
  ];

  return (
    <Transition.Root show={show} as={Fragment}>
      <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" initialFocus={cancelButtonRef} onClose={setOpen}>
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-neutral-600 opacity-80" />
          </Transition.Child>

          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block relative z-20 align-bottom bg-white rounded-lg px-8 pt-6 pb-6 text-left overflow-hidden shadow-lg transition-all sm:align-middle sm:max-w-md sm:w-full">
              <Dialog.Title as="h3" className="text-lg font-bold text-neutral-800 mb-1">
                Sync Data to Corporation
              </Dialog.Title>
              <p className="text-sm text-neutral-500 mb-4">
                Push enriched data from
                {' '}
                <span className="font-semibold text-neutral-700">{company.name}</span>
                {' '}
                to
                {' '}
                <span className="font-semibold text-neutral-700">{company.linkedCorporationName}</span>
                &apos;s profile.
              </p>
              <p className="text-xs text-warning-600 bg-warning-50 rounded-md px-3 py-2 mb-4 font-semibold">
                This will overwrite the corresponding fields on the corporation profile. Unlinking the enriched lead later restores
                those field groups to how they were before the first sync of each group.
              </p>

              <div className="space-y-2">
                {options.map(({ key, label, available }) => (
                  <label
                    key={key}
                    className={`flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors ${
                      available ? 'hover:bg-neutral-25' : 'opacity-40 cursor-not-allowed'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={syncOptions[key] && available}
                      onChange={() => available && toggleOption(key)}
                      disabled={!available}
                      className="h-4 w-4 text-main-600 border-neutral-300 rounded focus:ring-main-500"
                    />
                    <span className="text-sm font-semibold text-neutral-700">{label}</span>
                    {!available && <span className="text-xs text-neutral-400">(no data)</span>}
                  </label>
                ))}
              </div>

              <div className="mt-5 flex justify-end gap-3">
                <button
                  type="button"
                  className="inline-flex justify-center rounded-md border border-neutral-300 py-2.5 px-5 bg-white text-sm font-semibold text-main-600 hover:bg-neutral-25 focus:outline-none transition-colors"
                  onClick={() => setOpen(false)}
                  ref={cancelButtonRef}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={isLoading}
                  onClick={handleSync}
                  className={`inline-flex items-center py-2.5 px-5 rounded-md border border-transparent text-sm font-semibold text-white transition-colors ${
                    isLoading ? 'bg-main-300 cursor-not-allowed animate-pulse' : 'bg-main-600 hover:bg-main-700'
                  }`}
                >
                  {isLoading ? (
                    <ArrowPathIcon className="h-4 w-4 text-white mr-2 animate-spin" />
                  ) : (
                    <ArrowsRightLeftIcon className="h-4 w-4 text-white mr-2" />
                  )}
                  Sync Data
                </button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
